import { initializeApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";
import { getFirestore, Firestore } from "firebase/firestore";
import { initializeAppCheck, ReCaptchaV3Provider, getToken, AppCheck } from "firebase/app-check";
import { firebaseConfigSettings, RECAPTCHA_V3_SITE_KEY } from "./firebaseConfig";
import { AppCheckUnavailableError, withTimeout } from "./orderErrors";

// Initialize Firebase with error handling
let app;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let appCheckInstance: AppCheck | null = null;

// Check if keys are still default placeholders
const isConfigured = firebaseConfigSettings.apiKey !== "ZDE_VLOZTE_API_KEY" &&
                     firebaseConfigSettings.projectId !== "VSE_PROJEKT_ID";
// The key is declared as a string literal in firebaseConfig.ts, which TypeScript then refuses to compare with the placeholder.
const isRecaptchaConfigured = (RECAPTCHA_V3_SITE_KEY as string) !== "ZDE_VLOZTE_RECAPTCHA_V3_SITE_KEY";

if (!isConfigured) {
  console.warn("Firebase configuration is using placeholders. Please edit lib/firebaseConfig.ts");
}

try {
    // Even if placeholders are present, we try to init to avoid crash on import,
    // but auth operations will likely fail if keys are invalid.
    app = initializeApp(firebaseConfigSettings);
    authInstance = getAuth(app);
    dbInstance = getFirestore(app);

    if (isConfigured && isRecaptchaConfigured) {
        // Local dev has no verifiable reCAPTCHA domain — use a debug token instead
        // (register it once in Firebase Console -> App Check -> Apps -> Manage debug tokens).
        if (import.meta.env.DEV) {
            (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }
        appCheckInstance = initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(RECAPTCHA_V3_SITE_KEY),
            isTokenAutoRefreshEnabled: true,
        });
    } else if (isConfigured) {
        console.warn("reCAPTCHA v3 site key is a placeholder. Please edit lib/firebaseConfig.ts — /api/* calls will be rejected by the server until this is set.");
    }

    if (isConfigured) {
        console.log("Firebase initialized successfully");
    }
} catch (error) {
    console.error("Firebase initialization error:", error);
    authInstance = null;
    dbInstance = null;
}

export const auth = authInstance;
export const db = dbInstance;

// How long to wait for an App Check token. With reCAPTCHA blocked (ad/privacy blocker, firewall) the
// SDK never settles, which used to leave the order button spinning forever without any message.
export const APP_CHECK_TIMEOUT_MS = 15_000;

async function fetchAppCheckToken(): Promise<string> {
    if (!appCheckInstance) throw new AppCheckUnavailableError("App Check is not initialized.");
    try {
        const { token } = await withTimeout(
            getToken(appCheckInstance, false),
            APP_CHECK_TIMEOUT_MS,
            () => new AppCheckUnavailableError(`App Check token not received within ${APP_CHECK_TIMEOUT_MS / 1000} s.`),
        );
        return token;
    } catch (error) {
        if (error instanceof AppCheckUnavailableError) throw error;
        throw new AppCheckUnavailableError(`App Check token fetch failed: ${(error as Error)?.message ?? error}`);
    }
}

// Fetches a fresh Firebase App Check token and returns it as a ready-to-spread
// fetch() headers object. Backend routes (/api/upload-order, /api/generate-pdf, …)
// reject requests missing this header — see requireAppCheck in server.js.
// Never throws: without a token it returns {} and the backend answers 401.
export async function getAppCheckHeader(): Promise<Record<string, string>> {
    try {
        return { "X-Firebase-AppCheck": await fetchAppCheckToken() };
    } catch (error) {
        console.error("App Check token fetch failed:", error);
        return {};
    }
}

// Same, but throws AppCheckUnavailableError when there is no token — for the order, where sending a
// request that is certain to be rejected would only make the customer wait for a useless answer.
export async function getAppCheckHeaderOrThrow(): Promise<Record<string, string>> {
    return { "X-Firebase-AppCheck": await fetchAppCheckToken() };
}

// Attaches the current user's Firebase ID token, when logged in, so the backend
// can recognize administrators (e.g. to exempt them from the AI generation quota).
export async function getAuthHeader(): Promise<Record<string, string>> {
    if (!authInstance?.currentUser) return {};
    try {
        const token = await authInstance.currentUser.getIdToken();
        return { Authorization: `Bearer ${token}` };
    } catch (error) {
        console.error("ID token fetch failed:", error);
        return {};
    }
}
